import React from "react";
import { flushSync } from "react-dom";
import { Printer, Lock, FileText } from "lucide-react";
import { waitForPrintReady } from "../utils/waitForPrintReady.js";
import { buildV3TaxReportPrintUrl } from "../constants/v3taxBranding.js";

export default function PdfExportButton({
  label = "PDF Export",
  subtitle = "",
  badge = "",
  variant = "calc",
  disabled = false,
  disabledReason = "",
  language = "de",
  className = "",
  onPrintVariant,
  emphasis = "default",
  reportYearLabel = null,
}) {
  const handleClick = async () => {
    if (disabled) return;

    const year =
      reportYearLabel ||
      new URLSearchParams(window.location.search).get("year") ||
      new Date().getFullYear();
    const safeYear = String(year).replace(/[^a-zA-Z0-9._-]+/g, "_");

    const prevTitle = document.title;

    const nextTitle =
      variant === "raw"
        ? language === "en"
          ? `V3Tax_Appendix_2_Classified_Ledger_${safeYear}`
          : `V3Tax_Anlage_2_Klassifiziertes_Ledger_${safeYear}`
        : variant === "income"
          ? language === "en"
            ? `V3Tax_Income_Report_${safeYear}`
            : `V3Tax_Einkuenfte_Report_${safeYear}`
          : variant === "disposal"
            ? language === "en"
              ? `V3Tax_Capital_Gains_Report_${safeYear}`
              : `V3Tax_Veraeusserungs_Report_${safeYear}`
            : language === "en"
              ? `V3Tax_Appendix_1_Calculation_Report_${safeYear}`
              : `V3Tax_Anlage_1_Berechnungsreport_${safeYear}`;

    const prevPrintAttr = document.body.getAttribute("data-v3tax-print");
    const prevVariantAttr = document.body.getAttribute("data-v3tax-pdf-variant");
    const prevHref = window.location.href;
    const printUrl = buildV3TaxReportPrintUrl();

    document.title = nextTitle;

    if (onPrintVariant) {
      flushSync(() => onPrintVariant(variant));
    }

    document.body.setAttribute("data-v3tax-print", "report");
    document.body.setAttribute("data-v3tax-pdf-variant", variant);

    // Browser print headers/footers show location.href — use a clean origin
    // without /analyze/:datasetId so customer PDFs do not leak staging paths.
    try {
      window.history.replaceState(window.history.state, "", printUrl);
    } catch (_) {
      /* ignore */
    }

    const cleanup = () => {
      document.title = prevTitle;

      try {
        window.history.replaceState(window.history.state, "", prevHref);
      } catch (_) {
        /* ignore */
      }

      if (prevPrintAttr == null) {
        document.body.removeAttribute("data-v3tax-print");
      } else {
        document.body.setAttribute("data-v3tax-print", prevPrintAttr);
      }

      if (prevVariantAttr == null) {
        document.body.removeAttribute("data-v3tax-pdf-variant");
      } else {
        document.body.setAttribute("data-v3tax-pdf-variant", prevVariantAttr);
      }

      window.removeEventListener("afterprint", cleanup);
    };

    window.addEventListener("afterprint", cleanup, { once: true });

    await waitForPrintReady();
    window.print();
  };

  const isCard = Boolean(subtitle || badge);

  if (!isCard) {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={disabled && disabledReason ? disabledReason : undefined}
        aria-label={disabled && disabledReason ? disabledReason : label}
        className={`group inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl shadow-sm whitespace-nowrap transition-all border ${
          disabled
            ? "bg-slate-200/70 text-slate-500 border-slate-200 cursor-not-allowed opacity-70"
            : "bg-gradient-to-r from-blue-600 to-blue-800 text-white border-blue-700/40 hover:shadow-md hover:brightness-[1.03]"
        } ${className}`}
      >
        {disabled ? <Lock className="w-4 h-4" /> : <Printer className="w-4 h-4" />}
        <span className="font-semibold tracking-tight">{label}</span>
      </button>
    );
  }

  const cardTone =
    emphasis === "primary"
      ? "border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 hover:border-blue-300 hover:shadow-md"
      : emphasis === "soft"
        ? "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50 hover:border-emerald-300 hover:shadow-md"
        : "border-slate-200 bg-white/85 hover:border-slate-300 hover:shadow-md";

  const iconTone =
    emphasis === "primary"
      ? "bg-blue-600 text-white"
      : emphasis === "soft"
        ? "bg-emerald-600 text-white"
        : "bg-slate-900 text-white";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={disabled && disabledReason ? disabledReason : undefined}
      aria-label={disabled && disabledReason ? disabledReason : label}
      className={`group flex h-full w-full items-start gap-3 rounded-2xl border p-3.5 text-left shadow-sm transition-all ${
        disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-100/70 opacity-70"
          : cardTone
      } ${className}`}
    >
      <span
        className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-sm ${
          disabled ? "bg-slate-300 text-slate-600" : iconTone
        }`}
      >
        {disabled ? <Lock className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-start justify-between gap-2">
          <span className="block text-sm font-semibold leading-tight text-slate-950">
            {label}
          </span>

          {badge ? (
            <span className="shrink-0 rounded-full border border-slate-200 bg-white/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
              {badge}
            </span>
          ) : null}
        </span>

        {subtitle ? (
          <span className="mt-1 block text-xs leading-snug text-slate-500">
            {subtitle}
          </span>
        ) : null}

        <span
          className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold ${
            disabled
              ? "text-slate-500"
              : "text-blue-700"
          }`}
        >
          {disabled
            ? <Lock className="h-3.5 w-3.5" />
            : <Printer className="h-3.5 w-3.5" />}

          {disabled && disabledReason
            ? disabledReason
            : (
                language === "en"
                  ? "Export PDF"
                  : "PDF exportieren"
              )}
        </span>
      </span>
    </button>
  );
}
